import React, { useEffect, useRef } from 'react';
import { X, Sparkles } from 'lucide-react';
import { Button, ScrollShadow, Divider } from '@heroui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGuideBuilderGuide } from '@/contexts/GuideBuilderContext';
import { useGuideAIChat } from '@/hooks/useGuideAIChat';
import ChatMessage from './ChatMessage';
import ChatInput, { ChatInputHandle } from './ChatInput';
import StepFocusIndicator from './StepFocusIndicator';

interface GuideAIChatProps {
  isOpen: boolean;
  onClose: () => void;
}

const GuideAIChat: React.FC<GuideAIChatProps> = ({ isOpen, onClose }) => {
  const { guide } = useGuideBuilderGuide();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);

  const { messages, isStreaming, isSessionLoading, focusedStepId, setFocusedStepId, sendMessage, undo, canUndo } =
    useGuideAIChat(guide.id!, guide);
  // Focus input and scroll to bottom when chat opens
  useEffect(() => {
    if (isOpen && !isSessionLoading) {
      // Small delay to ensure the component is fully rendered
      const timeoutId = setTimeout(() => {
        chatInputRef.current?.focus();
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, isSessionLoading]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // // Only show chat when language is English
  // if (currentLanguage !== 'en') {
  //   return (
  //     <AnimatePresence>
  //       {isOpen && (
  //         <motion.div
  //           initial={{ opacity: 0, y: 20, scale: 0.95 }}
  //           animate={{ opacity: 1, y: 0, scale: 1 }}
  //           exit={{ opacity: 0, y: 20, scale: 0.95 }}
  //           transition={{ duration: 0.2 }}
  //           className="fixed bottom-4 right-4 w-96 h-[600px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 flex flex-col overflow-hidden"
  //         >
  //           <div className="flex-1 flex items-center justify-center p-6">
  //             <div className="text-center">
  //               <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
  //               <p className="text-gray-600 dark:text-gray-400">
  //                 AI Chat is only available in English mode. Please switch to English to use this feature.
  //               </p>
  //             </div>
  //           </div>
  //         </motion.div>
  //       )}
  //     </AnimatePresence>
  //   );
  // }

  if (!isOpen) return null;

  // Get focused step title
  const focusedStep = guide.steps.find((s) => s.id === focusedStepId);
  const focusedStepTitle = focusedStep?.title;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed bottom-4 right-4 w-96 h-[600px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">AI Guide Assistant</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Powered by AI</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* {canUndo && (
                <Button
                  isIconOnly
                  variant="light"
                  size="sm"
                  onPress={undo}
                  aria-label="Undo last change"
                  className="text-gray-600 dark:text-gray-400"
                >
                  <Undo2 className="w-4 h-4" />
                </Button>
              )} */}
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={onClose}
                aria-label="Close chat"
                className="text-gray-600 dark:text-gray-400"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Step Focus Indicator */}
          {focusedStepId && (
            <div className="px-4 pt-3">
              <StepFocusIndicator
                stepTitle={focusedStepTitle}
                stepId={focusedStepId}
                onClear={() => setFocusedStepId(undefined)}
              />
            </div>
          )}

          {/* Messages */}
          <ScrollShadow className="flex-1 overflow-y-auto p-4 bg-gray-50/50 dark:bg-gray-950/50" ref={chatContainerRef}>
            {isSessionLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-primary-500 animate-pulse" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Initializing chat session...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Start a conversation</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  Ask questions, upload files, or request guide generation.
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  The AI assistant is here to help you create amazing guides.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollShadow>

          <Divider />

          {/* Input */}
          <ChatInput
            ref={chatInputRef}
            onSend={(message, attachments) => sendMessage(message, attachments, focusedStepId)}
            disabled={isStreaming || isSessionLoading}
            placeholder={isStreaming ? 'AI is thinking...' : 'Ask a question or request guide generation...'}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GuideAIChat;
